import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { IGetServerSidePropsData } from './index.types'
import { client } from '@/libs/apollo'
import styled from 'styled-components'
import { FormProvider, useForm } from 'react-hook-form'

import Head from '@/components/head/head.component'
import PostLayout from '@/layouts/post/post.layout'
import { Button } from '@/components/button/button.component'
import Input from '@/components/input/input.component'
import Textarea from '@/components/textarea/textarea.component'

import {
  CATEGORIES,
  ICategoriesQueryData,
} from '@/graphql/category/category.queries'
import {
  IPostsQuery,
  IPostsQueryVars,
  POSTS_QUERY,
} from '@/graphql/post/post.queries'

export const getServerSideProps: GetServerSideProps<IGetServerSidePropsData> =
  async (context) => {
    const categoriesData = await client.query<ICategoriesQueryData>({
      query: CATEGORIES,
    })

    const { data: postsData } = await client.query<
      IPostsQuery,
      IPostsQueryVars
    >({
      query: POSTS_QUERY,
      variables: {
        query: {
          limit: 5,
          offset: 0,
        },
      },
    })

    const { data: patreonPostsData } = await client.query<
      IPostsQuery,
      IPostsQueryVars
    >({
      query: POSTS_QUERY,
      variables: {
        query: {
          limit: 10,
          offset: 0,
          isPublic: false,
        },
      },
    })

    return {
      props: {
        categories: categoriesData.data.categories.items,
        patreonPosts: patreonPostsData.posts.items,
        latestPosts: postsData.posts.items,
      },
    }
  }

export default function ContactPage({
  categories,
  latestPosts,
  patreonPosts,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const methods = useForm({
    defaultValues: {
      name: '',
      email: '',
      message: '',
    },
  })

  const onSubmit = methods.handleSubmit(async (form) => {
    console.log(form)

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
        }),
      })

      const data = await res.json()

      console.log(data)
      methods.reset()
      alert('Správa bola odoslaná.')
    } catch (err) {
      console.log(err)
      alert('Niečo sa pokazilo, skúste znova neskôr.')
    }
  })

  return (
    <PostLayout
      categories={categories ?? []}
      latestPosts={latestPosts ?? []}
      patreonPosts={patreonPosts ?? []}
    >
      <FormProvider {...methods}>
        <Head
          title="Nehanbi sa a ozvi sa - rada ti pomôžem a poradím | IVETARS"
          description="Na Instagrame som tu pre teba @ivetar.s blog. Ak ti viac vyhovuje Facebook, kontaktuj ma v správe na stránke IVETARS. Nehanbi sa a pytajsa@ivetars.com. "
          image="https://res.cloudinary.com/coderkin/image/upload/v1680204771/ivetars-blog/meta-placeholder_zi2zjr.png"
        ></Head>
        <Title>Kontakt</Title>
        <Container>
          <p>
            Rada ti pomôžem alebo poradím so všetkými možnými otázkami alebo
            požiadavkami, ktoré sa týkajú preberaných tém. S priateľmi, známymi
            aj mojimi sledovateľmi máme množstvo zaujímavých debát.
          </p>
          <p>
            Na Instagrame som tu pre teba na{' '}
            <a
              href="https://www.instagram.com/ivetar.s_blog/"
              target="_blank"
              rel="noopener noreferrer"
            >
              @ivetar.s_blog
            </a>
            . Ak ti viac vyhovuje Facebook, môžeš ma kontaktovať do správy na
            stránke{' '}
            <a
              href="https://www.facebook.com/ivetars"
              target="_blank"
              rel="noopener noreferrer"
            >
              IVETARS
            </a>
            .
          </p>
          <p>
            Alebo používaš radšej mail? Nehanbi sa a{' '}
            <a href="mailto:pytajsa@ivetars.com">pytajsa@ivetars.com</a>.
          </p>
          <p>Dohodneme sa a pokecáme v dohodnutom čase!</p>
          <p></p>
          <p>iF.</p>

          <form onSubmit={onSubmit}>
            <Input
              label="Tvoje meno a priezvisko"
              name="name"
              type="text"
              required
            />
            <Input
              label="Tvoja e-mailová adresa"
              name="email"
              type="email"
              required
            />
            <Textarea
              label="Čo sa chceš opýtať"
              name="message"
              rows={10}
              required
            />

            <Button left="auto" top="2rem">
              Odoslať správu
            </Button>
          </form>
        </Container>
      </FormProvider>
    </PostLayout>
  )
}

const Title = styled.h1`
  font-size: 3rem;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 5rem;
  line-height: 1.25;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 6.2rem;
  }
`

const Container = styled.div`
  max-width: 62rem;
  margin: 0 auto;

  img {
    width: 100%;
    max-height: 30rem;
    margin-bottom: 2rem;
    object-fit: contain;
  }

  blockquote {
    font-size: 1.6rem;
    border-left: 0.5rem solid ${({ theme }) => theme.fonts.secondary};
    padding-left: 2rem;

    &:not(:last-child) {
      padding-bottom: 1rem;
    }

    &:last-child {
      margin-bottom: 4rem;
    }
  }

  p {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
    font-weight: 300;
  }

  h1 {
    font-size: 3.2rem;
    line-height: 1.2;
    margin-bottom: 2rem;
  }

  h2 {
    font-size: 2.8rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h3 {
    font-size: 2.4rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h4 {
    font-size: 2rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h5 {
    font-size: 1.6rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h6 {
    font-size: 1.2rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  ul {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }

  ol {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }

  li {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }

  a {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.fonts.secondary};

    &:hover {
      text-decoration: underline;
    }
  }

  /* form {
    padding: 1.5rem 1.5rem 3rem;
    background-color: ${({ theme }) => theme.colors.primary};
  } */
`
